import React, { useMemo, useState } from "react";
import { analyzeIracecontrolPdf } from "../../../lib/ligamanagerApi";

function prettyErr(error) {
    if (!error) return "Unbekannter Fehler";
    if (typeof error === "string") return error;
    if (error instanceof Error) return error.message || "Fehler";
    if (typeof error === "object") {
        if (error.detail) return typeof error.detail === "string" ? error.detail : JSON.stringify(error.detail);
        if (error.message) return error.message;
    }
    return String(error);
}

function normalizeCarNumber(value) {
    const raw = String(value ?? "").replace("#", "").trim();
    if (/^\d+$/.test(raw)) return Number(raw);
    return Number.MAX_SAFE_INTEGER;
}

function statusFor(row) {
    if (!row?.auto_safe) return { label: "Manuell prüfen", tone: "warn" };
    return { label: "Eindeutig", tone: "ok" };
}

function csvEscape(value) {
    const text = String(value ?? "");
    if (/[";\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
    return text;
}

export default function IracecontrolPdfTest() {
    const [file, setFile] = useState(null);
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [query, setQuery] = useState("");

    const rows = useMemo(() => {
        const source = Array.isArray(data?.drivers) ? [...data.drivers] : [];
        source.sort((a, b) => {
            const carDiff = normalizeCarNumber(a?.car_number) - normalizeCarNumber(b?.car_number);
            if (carDiff !== 0) return carDiff;
            return String(a?.driver_name || "").localeCompare(String(b?.driver_name || ""), "de");
        });

        const q = String(query || "").trim().toLowerCase();
        if (!q) return source;
        return source.filter((row) =>
            String(row?.driver_name || "").toLowerCase().includes(q) ||
            String(row?.car_number || "").toLowerCase().includes(q)
        );
    }, [data, query]);

    const totals = useMemo(() => {
        const all = Array.isArray(data?.drivers) ? data.drivers : [];
        return all.reduce((acc, row) => {
            acc.incidents += Number(row?.pdf_total_incident_points || 0);
            acc.offtracks += Number(row?.pdf_offtracks_before_finish || 0);
            acc.nonOfftracks += Number(row?.pdf_non_offtrack_incidents || 0);
            acc.postFinish += Number(row?.pdf_post_finish_1x || 0);
            return acc;
        }, { incidents: 0, offtracks: 0, nonOfftracks: 0, postFinish: 0 });
    }, [data]);

    async function analyze(nextFile) {
        setFile(nextFile || null);
        setData(null);
        setError("");
        if (!nextFile) return;

        setBusy(true);
        try {
            const result = await analyzeIracecontrolPdf(nextFile);
            setData(result || null);
        } catch (err) {
            setError(`iRaceControl-PDF konnte nicht ausgewertet werden: ${prettyErr(err)}`);
        } finally {
            setBusy(false);
        }
    }

    function exportCsv() {
        const source = Array.isArray(data?.drivers) ? data.drivers : [];
        if (!source.length) return;

        const header = [
            "Startnummer",
            "Fahrer",
            "PDF Incidentpunkte",
            "1x gesamt",
            "1x bis Ziel / Offtracks",
            "2x",
            "3x",
            "4x",
            "1x->2x",
            "1x->4x",
            "2x->4x",
            "1x nach Ziel ignoriert",
            "Nicht-Offtrack-Abzug",
            "Across the line erkannt",
            "Status",
            "PDF-Seiten",
        ];

        const body = source.map((row) => [
            row?.car_number,
            row?.driver_name,
            row?.pdf_total_incident_points,
            row?.pdf_1x,
            row?.pdf_offtracks_before_finish,
            row?.pdf_2x,
            row?.pdf_3x,
            row?.pdf_4x,
            row?.pdf_1x_to_2x,
            row?.pdf_1x_to_4x,
            row?.pdf_2x_to_4x,
            row?.pdf_post_finish_1x,
            row?.pdf_non_offtrack_incidents,
            row?.across_line_seen ? "ja" : "nein",
            row?.auto_safe ? "eindeutig" : "manuell prüfen",
            Array.isArray(row?.pages) ? row.pages.join(",") : "",
        ]);

        const csv = [header, ...body]
            .map((row) => row.map(csvEscape).join(";"))
            .join("\r\n");
        const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const base = String(file?.name || "iracecontrol-report").replace(/\.pdf$/i, "");
        link.download = `${base}-incident-test.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    }

    return (
        <div className="rv-page manager-modern-page">
            <section className="rv-page-hero">
                <div className="rv-page-hero__inner">
                    <div>
                        <div className="rv-page-kicker">Orga · Testmodus</div>
                        <h1 className="rv-page-title">iRaceControl PDF testen</h1>
                        <p className="rv-page-subtitle">
                            Alte iRaceControl Race Reports unabhängig von einem laufenden Rennen auswerten. Dieser Testmodus schreibt nichts in die Datenbank und verändert keine Results.
                        </p>
                    </div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <button className="btn" type="button" onClick={() => window.close()}>
                            Fenster schließen
                        </button>
                    </div>
                </div>
            </section>

            {error && <div className="rv-message rv-message--error">{error}</div>}

            <section className="card" style={{ display: "grid", gap: 14 }}>
                <div>
                    <h2 style={{ margin: 0 }}>PDF auswählen</h2>
                    <p className="muted" style={{ margin: "6px 0 0" }}>
                        Es wird ausschließlich der Bereich <b>EVENTS → RACE</b> ausgewertet. Ein 1x nach der Meldung <b>Across the line!</b> wird nicht als Offtrack gezählt.
                    </p>
                </div>

                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                    <input
                        id="rv-iracecontrol-test-pdf"
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={(event) => analyze(event.target.files?.[0] || null)}
                        style={{ display: "none" }}
                        disabled={busy}
                    />
                    <label
                        htmlFor="rv-iracecontrol-test-pdf"
                        className="btn btn--accent"
                        style={{ cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1 }}
                    >
                        {busy ? "PDF wird analysiert..." : file ? "Andere PDF auswählen" : "iRaceControl PDF öffnen"}
                    </label>
                    <div
                        style={{
                            padding: "9px 12px",
                            borderRadius: 12,
                            border: "1px solid rgba(255,255,255,.14)",
                            background: "rgba(255,255,255,.03)",
                            maxWidth: 520,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                        title={file?.name || ""}
                    >
                        {file?.name || "Keine PDF geladen"}
                    </div>
                    <button className="btn" type="button" onClick={exportCsv} disabled={!data?.drivers?.length || busy}>
                        CSV exportieren
                    </button>
                </div>
            </section>

            {!!data && (
                <>
                    <section className="card" style={{ display: "grid", gap: 12 }}>
                        <h2 style={{ margin: 0 }}>Auswertung</h2>
                        <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
                            <span>Fahrer: <b>{data?.driver_count ?? 0}</b></span>
                            <span>Eindeutig: <b>{data?.summary?.auto_safe_drivers ?? 0}</b></span>
                            <span>Manuell prüfen: <b>{data?.summary?.manual_review_drivers ?? 0}</b></span>
                            <span>PDF-Incidentpunkte: <b>{totals.incidents}</b></span>
                            <span>Offtracks bis Ziel: <b>{totals.offtracks}</b></span>
                            <span>Nicht-Offtrack-Abzug: <b>{totals.nonOfftracks}</b></span>
                            <span>1x nach Ziel ignoriert: <b>{totals.postFinish}</b></span>
                        </div>
                        <input
                            className="input"
                            type="search"
                            placeholder="Fahrer oder Startnummer suchen..."
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            style={{ maxWidth: 420 }}
                        />
                    </section>

                    <section className="card" style={{ display: "grid", gap: 12 }}>
                        <div style={{ overflowX: "auto" }}>
                            <table className="table table--wide" style={{ minWidth: 1450 }}>
                                <thead>
                                <tr>
                                    <th>Fahrer</th>
                                    <th>PDF Incidents</th>
                                    <th>1x bis Ziel</th>
                                    <th>2x</th>
                                    <th>3x</th>
                                    <th>4x</th>
                                    <th>Upgrades</th>
                                    <th>1x nach Ziel</th>
                                    <th>Abzug</th>
                                    <th>Offtracks</th>
                                    <th>Ziel erkannt</th>
                                    <th>Status</th>
                                </tr>
                                </thead>
                                <tbody>
                                {!rows.length && (
                                    <tr>
                                        <td colSpan={12} className="muted">Keine Fahrer gefunden.</td>
                                    </tr>
                                )}
                                {rows.map((row) => {
                                    const status = statusFor(row);
                                    const upgrades = [
                                        Number(row?.pdf_1x_to_2x || 0) ? `1→2: ${row.pdf_1x_to_2x}` : "",
                                        Number(row?.pdf_1x_to_4x || 0) ? `1→4: ${row.pdf_1x_to_4x}` : "",
                                        Number(row?.pdf_2x_to_4x || 0) ? `2→4: ${row.pdf_2x_to_4x}` : "",
                                    ].filter(Boolean).join(" · ");
                                    const unsupported = Array.isArray(row?.unsupported_incident_events)
                                        ? row.unsupported_incident_events.filter(Boolean)
                                        : [];

                                    return (
                                        <tr key={`${row?.car_number}-${row?.driver_name}`}>
                                            <td>
                                                {row?.car_number ? `#${row.car_number} ` : ""}
                                                <strong>{row?.driver_name || "-"}</strong>
                                                {!!row?.pages?.length && (
                                                    <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>
                                                        PDF S. {row.pages.join(", ")}
                                                    </div>
                                                )}
                                            </td>
                                            <td><strong>{row?.pdf_total_incident_points ?? 0}</strong></td>
                                            <td><strong>{row?.pdf_offtracks_before_finish ?? 0}</strong></td>
                                            <td>{row?.pdf_2x ?? 0}</td>
                                            <td>{row?.pdf_3x ?? 0}</td>
                                            <td>{row?.pdf_4x ?? 0}</td>
                                            <td>{upgrades || <span className="muted">-</span>}</td>
                                            <td>
                                                <strong>{row?.pdf_post_finish_1x ?? 0}</strong>
                                                {Number(row?.pdf_post_finish_1x || 0) > 0 && (
                                                    <div className="muted" style={{ fontSize: 11 }}>ignoriert</div>
                                                )}
                                            </td>
                                            <td>{row?.pdf_non_offtrack_incidents ?? 0}</td>
                                            <td><strong>{row?.pdf_offtracks_before_finish ?? 0}</strong></td>
                                            <td>{row?.across_line_seen ? "Ja" : "Nein"}</td>
                                            <td>
                                                <strong>{status.label}</strong>
                                                {!!unsupported.length && (
                                                    <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>
                                                        {unsupported.join(", ")}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </>
            )}
        </div>
    );
}
